import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TableWindow from '../../../../components/Table/TableWindow';
import useTablePages from '../../../../hooks/useTablePages';
import availableColumns from './Companies.columns';
import { useOrderBy } from '../../../../hooks/useOrderBy';
import PageBar from '../../../../components/PageBar/PageBar';

import { RouteComponentProps, withRouter } from 'react-router';
import useThirdPaneCallbacks from '../../../../hooks/useThirdPaneCallbacks';

import ThirdPaneLayout, {
  MainColumn,
} from '../../../../components/Layout/ThirdPaneLayout';
import FilterBarCompanies from './FilterBarCompanies';
import useMode from '../../../../hooks/useMode';
import useColumnConfiguration from '../../../../hooks/useColumnConfiguration';
import CompanyThirdPane from '../../../../components/Company/CompanyThirdPane';
import { CompanyItem, GET_COMPANIES, GET_COMPANY_IDS } from './Companies.query';
import Button from '../../../../components/Button/Button';
import { PortalWithState } from 'react-portal';
import { useHistory } from 'react-router-dom';
import CompanyEditModal from '../../../../components/Company/CompanyEditModal';

type CompanyDetailParams = {
  id?: string;
};

type CompanyDetailProps = RouteComponentProps<CompanyDetailParams>;

/**
 * @component Used for companies page
 */
function Companies({ match }: CompanyDetailProps): JSX.Element {
  const history = useHistory();
  const mode = useMode();

  const [filters, setFilters] = useState({} as Record<string, unknown> | null);

  const { orderBy, onHeaderClick } = useOrderBy(
    'companies',
    availableColumns,
    'name'
  );

  const { loading, itemIds, selectedIds, setSelectedIds, itemLoader, refetch } =
    useTablePages<CompanyItem>(
      'companies',
      GET_COMPANY_IDS,
      {
        where: filters,
        limit: null,
        orderBy,
      },
      GET_COMPANIES,
      (ids) => ({
        where: {
          id: ids,
        },
        limit: null,
        orderBy,
      })
    );

  const { closeThirdPane, onRowClick, prev, prevEnabled, next, nextEnabled } =
    useThirdPaneCallbacks(`/${useMode()}/companies`, itemIds, match.params.id);

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(availableColumns, 'companies');

  return (
    <ThirdPaneLayout paneVisible={!!match.params.id}>
      <MainColumn px="mainContent" pb="mainContent">
        <PageBar title="Companies">
          <PortalWithState closeOnEsc>
            {({ openPortal, closePortal, portal }) => (
              <>
                <Button
                  ml={3}
                  variant="primary"
                  icon="plus"
                  onClick={openPortal}
                >
                  Add Company
                </Button>
                {portal(
                  <CompanyEditModal
                    action="add"
                    onSave={(company) => {
                      history.push(`/${mode}/companies/${company.id}`);
                    }}
                    onClose={() => {
                      closePortal();
                      refetch();
                    }}
                  />
                )}
              </>
            )}
          </PortalWithState>
        </PageBar>
        <FilterBarCompanies
          filters={filters ?? {}}
          setFilters={setFilters}
          selectedId={match.params.id}
        />
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <TableWindow
            columns={visibleColumns}
            columnConfiguration={columnConfiguration}
            setColumnConfiguration={setColumnConfiguration}
            itemIds={itemIds}
            itemLoader={itemLoader}
            onSelectionChange={setSelectedIds}
            selectedIds={selectedIds}
            activeIds={match.params.id ? [match.params.id] : undefined}
            onHeaderClick={onHeaderClick}
            orderBy={orderBy}
            onRowClick={onRowClick}
          />
        )}
      </MainColumn>
      <CompanyThirdPane
        id={match.params.id}
        onPrev={prev}
        prevEnabled={prevEnabled}
        onNext={next}
        nextEnabled={nextEnabled}
        onToggle={closeThirdPane}
      />
    </ThirdPaneLayout>
  );
}

Companies.propTypes = {
  match: PropTypes.any,
};

export default withRouter(Companies);
